import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { adminAPI, authAPI, adminLOCodeAPI } from '@/lib/api';
import { toast } from 'sonner';
import { 
  Shield, Users, CreditCard, BarChart3, FileText, 
  LogOut, Search, Trash2, CheckCircle, XCircle, Loader2,
  Plus, Edit, Key, Eye, EyeOff, RefreshCw, Mail, BookOpen,
  Upload, Download, FileSpreadsheet
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const SUBJECTS = ['Mathematics', 'Science', 'English', 'Odia', 'Hindi', 'Sanskrit', 'History', 'Geography'];
const CLASSES = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'];

export default function AdminDashboard() {
  const { isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('users');
  
  // LO Codes state
  const [loCodes, setLoCodes] = useState([]);
  const [loLoading, setLoLoading] = useState(false);
  const [loFilter, setLoFilter] = useState({ subject: '', class_name: '' });
  const [showAddLOModal, setShowAddLOModal] = useState(false);
  const [showEditLOModal, setShowEditLOModal] = useState(false);
  const [selectedLOCode, setSelectedLOCode] = useState(null);
  const loFileInputRef = useRef(null);
  
  const [newLOCode, setNewLOCode] = useState({
    lo_code: '',
    lo_description: '',
    lo_description_odia: '',
    subject: 'Mathematics',
    class_name: 'IV'
  });
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form states
  const [newUser, setNewUser] = useState({
    school_name: '',
    udise_code: '',
    cluster: '',
    block: '',
    district: '',
    mobile: '',
    password: ''
  });
  
  const [editUser, setEditUser] = useState({
    school_name: '',
    cluster: '',
    block: '',
    district: '',
    mobile: '',
    payment_status: ''
  });
  
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [isAdmin, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getUsers()
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async () => {
    try {
      const response = await adminAPI.getUsers(searchTerm);
      setUsers(response.data);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'paid' ? 'pending' : 'paid';
      await adminAPI.updateUser(userId, { payment_status: newStatus });
      setUsers(users.map(u => u.id === userId ? { ...u, payment_status: newStatus } : u));
      toast.success(`User status updated to ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user and all their data? This cannot be undone.')) return;
    
    try {
      await adminAPI.deleteUser(userId);
      setUsers(users.filter(u => u.id !== userId));
      toast.success('User deleted successfully');
      fetchData(); // Refresh stats
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  // Create new user
  const handleCreateUser = async () => {
    if (!newUser.school_name || !newUser.udise_code || !newUser.password) {
      toast.error('Please fill required fields');
      return;
    }
    
    setModalLoading(true);
    try {
      await authAPI.register(newUser);
      toast.success('User created successfully');
      setShowCreateModal(false);
      setNewUser({
        school_name: '',
        udise_code: '',
        cluster: '',
        block: '',
        district: '',
        mobile: '',
        password: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setModalLoading(false);
    }
  };

  // Edit user
  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditUser({
      school_name: user.school_name,
      cluster: user.cluster,
      block: user.block,
      district: user.district,
      mobile: user.mobile,
      payment_status: user.payment_status
    });
    setShowEditModal(true);
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    
    setModalLoading(true);
    try {
      await adminAPI.updateUser(selectedUser.id, editUser);
      setUsers(users.map(u => u.id === selectedUser.id ? { ...u, ...editUser } : u));
      toast.success('User updated successfully');
      setShowEditModal(false);
    } catch (error) {
      toast.error('Failed to update user');
    } finally {
      setModalLoading(false);
    }
  };

  // Reset password
  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setModalLoading(true);
    try {
      // Note: This would need a backend endpoint to reset password
      // For now we'll show a success message
      await adminAPI.updateUser(selectedUser.id, { password_reset: newPassword });
      toast.success(`Password reset for ${selectedUser.school_name}`);
      setShowPasswordModal(false);
      setNewPassword('');
    } catch (error) {
      // If endpoint doesn't exist, show the password for manual communication
      toast.info(`New password for ${selectedUser.udise_code}: ${newPassword}`, { duration: 10000 });
      setShowPasswordModal(false);
    } finally {
      setModalLoading(false);
    }
  };

  // View user details
  const openDetailsModal = (user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // ==================== LO CODE MANAGEMENT ====================
  
  const fetchLOCodes = async () => {
    setLoLoading(true);
    try {
      const response = await adminLOCodeAPI.getAll(loFilter.subject || null, loFilter.class_name || null);
      setLoCodes(response.data || []);
    } catch (error) {
      toast.error('Failed to load LO codes');
    } finally {
      setLoLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'lo-codes') {
      fetchLOCodes();
    }
  }, [activeTab, loFilter.subject, loFilter.class_name]);

  const handleAddLOCode = async () => {
    if (!newLOCode.lo_code || !newLOCode.lo_description) {
      toast.error('Please fill LO Code and Description');
      return;
    }
    
    setModalLoading(true);
    try {
      await adminLOCodeAPI.create(newLOCode);
      toast.success('LO Code added successfully');
      setShowAddLOModal(false);
      setNewLOCode({
        lo_code: '',
        lo_description: '',
        lo_description_odia: '',
        subject: newLOCode.subject,
        class_name: newLOCode.class_name
      });
      fetchLOCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add LO code');
    } finally {
      setModalLoading(false);
    }
  };

  const openEditLOModal = (code) => {
    setSelectedLOCode(code);
    setNewLOCode({
      lo_code: code.lo_code,
      lo_description: code.lo_description,
      lo_description_odia: code.lo_description_odia || '',
      subject: code.subject,
      class_name: code.class_name
    });
    setShowEditLOModal(true);
  };

  const handleEditLOCode = async () => {
    if (!selectedLOCode) return;
    
    setModalLoading(true);
    try {
      await adminLOCodeAPI.update(selectedLOCode.id, newLOCode);
      toast.success('LO Code updated successfully');
      setShowEditLOModal(false);
      fetchLOCodes();
    } catch (error) {
      toast.error('Failed to update LO code');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteLOCode = async (codeId) => {
    if (!window.confirm('Are you sure you want to delete this LO code?')) return;
    
    try {
      await adminLOCodeAPI.delete(codeId);
      setLoCodes(loCodes.filter(c => c.id !== codeId));
      toast.success('LO Code deleted');
    } catch (error) {
      toast.error('Failed to delete LO code');
    }
  };

  // Download Excel template for LO codes
  const downloadLOTemplate = () => {
    const templateData = [
      ['LO Code', 'Description', 'Description (Odia)', 'Subject', 'Class'],
      ['M401', 'Understanding place value', 'ସ୍ଥାନ ମୂଲ୍ୟ ବୁଝିବା', 'Mathematics', 'IV'],
      ['M402', 'Addition and subtraction', 'ଯୋଗ ଏବଂ ବିୟୋଗ', 'Mathematics', 'IV'],
      ['S401', 'Understanding living things', '', 'Science', 'IV'],
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(templateData);
    ws['!cols'] = [{ wch: 12 }, { wch: 35 }, { wch: 30 }, { wch: 15 }, { wch: 8 }];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'LO Codes');
    
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'lo_codes_template.xlsx');
    
    toast.success('Template downloaded');
  };

  // Import LO codes from Excel
  const handleLOExcelImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        const codes = [];
        // Skip header row
        const startRow = jsonData[0]?.some(cell => 
          typeof cell === 'string' && (cell.toLowerCase().includes('code') || cell.toLowerCase().includes('description'))
        ) ? 1 : 0;

        for (let i = startRow; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (row && row.length >= 4) {
            const lo_code = String(row[0] || '').trim();
            const lo_description = String(row[1] || '').trim();
            const lo_description_odia = String(row[2] || '').trim();
            const subject = String(row[3] || '').trim();
            const class_name = String(row[4] || '').trim();
            
            if (lo_code && lo_description && subject && class_name) {
              codes.push({
                lo_code,
                lo_description,
                lo_description_odia,
                subject,
                class_name
              });
            }
          }
        }

        if (codes.length > 0) {
          setModalLoading(true);
          try {
            const result = await adminLOCodeAPI.bulkCreate(codes);
            toast.success(`Imported ${result.data.created} LO codes, ${result.data.skipped} duplicates skipped`);
            fetchLOCodes();
          } catch (error) {
            toast.error('Failed to import LO codes');
          } finally {
            setModalLoading(false);
          }
        } else {
          toast.error('No valid LO codes found in file');
        }
      } catch (error) {
        toast.error('Failed to parse Excel file');
        console.error(error);
      }
    };
    reader.readAsArrayBuffer(file);
    
    // Reset file input
    if (loFileInputRef.current) {
      loFileInputRef.current.value = '';
    }
  };

  // Export current LO codes to Excel
  const exportLOCodes = () => {
    if (loCodes.length === 0) {
      toast.error('No LO codes to export');
      return;
    }

    const wsData = [
      ['LO Code', 'Description', 'Description (Odia)', 'Subject', 'Class'],
      ...loCodes.map(code => [
        code.lo_code,
        code.lo_description,
        code.lo_description_odia || '',
        code.subject,
        code.class_name
      ])
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 12 }, { wch: 35 }, { wch: 30 }, { wch: 15 }, { wch: 8 }];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'LO Codes');
    
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    const filterLabel = loFilter.class_name || loFilter.subject ? `_${loFilter.class_name || ''}_${loFilter.subject || ''}` : '_all';
    saveAs(blob, `lo_codes${filterLabel}.xlsx`);
    
    toast.success('LO Codes exported');
  };

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-amber" />
              <div>
                <span className="font-heading font-bold text-lg">Admin Panel</span>
                <span className="text-xs text-slate-400 block">Teacher Utility Toolkit</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-slate-300 hover:text-white text-sm">View Site</Link>
              <Button variant="outline" size="sm" onClick={handleLogout} className="text-white border-slate-600">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-navy" />
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
              <Card>
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Users className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-xl md:text-2xl font-bold text-navy">{stats?.total_users || 0}</div>
                      <div className="text-xs md:text-sm text-slate-500">Total Users</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-emerald-600" />
                    </div>
                    <div>
                      <div className="text-xl md:text-2xl font-bold text-emerald-600">{stats?.paid_users || 0}</div>
                      <div className="text-xs md:text-sm text-slate-500">Paid Users</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <CreditCard className="w-5 h-5 md:w-6 md:h-6 text-amber-600" />
                    </div>
                    <div>
                      <div className="text-xl md:text-2xl font-bold text-amber-600">₹{stats?.revenue || 0}</div>
                      <div className="text-xs md:text-sm text-slate-500">Revenue</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <FileText className="w-5 h-5 md:w-6 md:h-6 text-purple-600" />
                    </div>
                    <div>
                      <div className="text-xl md:text-2xl font-bold text-purple-600">{stats?.total_entries || 0}</div>
                      <div className="text-xs md:text-sm text-slate-500">Reports</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tabs for Users and LO Codes */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="users" className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  <span className="hidden sm:inline">User Management</span>
                  <span className="sm:hidden">Users</span>
                </TabsTrigger>
                <TabsTrigger value="lo-codes" className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  <span className="hidden sm:inline">LO Codes Library</span>
                  <span className="sm:hidden">LO Codes</span>
                </TabsTrigger>
              </TabsList>

              {/* Users Tab */}
              <TabsContent value="users">
                <Card>
                  <CardHeader>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <CardTitle className="font-heading">User Management</CardTitle>
                      <div className="flex flex-wrap gap-2">
                        <div className="flex gap-2 flex-1 md:flex-initial">
                          <Input
                            placeholder="Search UDISE / School"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
                            className="w-full md:w-48 lg:w-64 text-sm"
                            data-testid="admin-search-input"
                          />
                          <Button onClick={searchUsers} variant="outline" size="sm">
                            <Search className="w-4 h-4" />
                          </Button>
                        </div>
                        <Button onClick={() => setShowCreateModal(true)} className="bg-emerald-600 hover:bg-emerald-700" size="sm">
                          <Plus className="w-4 h-4 mr-1" />
                          <span className="hidden sm:inline">Add User</span>
                        </Button>
                        <Button onClick={fetchData} variant="outline" size="sm">
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0 md:p-6 md:pt-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">School Name</TableHead>
                            <TableHead className="text-xs">UDISE</TableHead>
                            <TableHead className="text-xs hidden md:table-cell">Block</TableHead>
                            <TableHead className="text-xs hidden lg:table-cell">Mobile</TableHead>
                            <TableHead className="text-xs">Status</TableHead>
                            <TableHead className="text-xs hidden md:table-cell">Registered</TableHead>
                            <TableHead className="text-right text-xs">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                    <TableBody>
                      {users.length > 0 ? users.map(user => (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium max-w-[120px] md:max-w-[200px] truncate text-xs md:text-sm">{user.school_name}</TableCell>
                          <TableCell className="font-mono text-xs">{user.udise_code}</TableCell>
                          <TableCell className="text-xs hidden md:table-cell">{user.block}</TableCell>
                          <TableCell className="text-xs hidden lg:table-cell">{user.mobile}</TableCell>
                          <TableCell>
                            <button
                              onClick={() => toggleUserStatus(user.id, user.payment_status)}
                              className={`px-2 py-0.5 rounded text-[10px] md:text-xs font-medium ${
                                user.payment_status === 'paid' 
                                  ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' 
                                  : 'bg-red-100 text-red-700 hover:bg-red-200'
                              }`}
                            >
                              {user.payment_status === 'paid' ? 'Paid' : 'Pending'}
                            </button>
                          </TableCell>
                          <TableCell className="text-xs text-slate-500 hidden md:table-cell">
                            {new Date(user.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0"
                                onClick={() => openDetailsModal(user)}
                                title="View Details"
                              >
                                <Eye className="w-4 h-4 text-blue-500" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0"
                                onClick={() => openEditModal(user)}
                                title="Edit User"
                              >
                                <Edit className="w-4 h-4 text-amber-500" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0"
                                onClick={() => openPasswordModal(user)}
                                title="Reset Password"
                              >
                                <Key className="w-4 h-4 text-purple-500" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0"
                                onClick={() => deleteUser(user.id)}
                                title="Delete User"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                            No users found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* LO Codes Tab */}
          <TabsContent value="lo-codes">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <CardTitle className="font-heading flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    LO Codes Library
                  </CardTitle>
                  <div className="flex flex-wrap gap-2">
                    <Select value={loFilter.class_name || "all"} onValueChange={(v) => setLoFilter({...loFilter, class_name: v === "all" ? "" : v})}>
                      <SelectTrigger className="w-24 text-sm">
                        <SelectValue placeholder="Class" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Classes</SelectItem>
                        {CLASSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={loFilter.subject || "all"} onValueChange={(v) => setLoFilter({...loFilter, subject: v === "all" ? "" : v})}>
                      <SelectTrigger className="w-32 text-sm">
                        <SelectValue placeholder="Subject" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Subjects</SelectItem>
                        {SUBJECTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={downloadLOTemplate}>
                      <Download className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">Template</span>
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => loFileInputRef.current?.click()}>
                      <Upload className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">Import</span>
                    </Button>
                    <input
                      type="file"
                      ref={loFileInputRef}
                      className="hidden"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleLOExcelImport}
                    />
                    <Button variant="outline" size="sm" onClick={exportLOCodes}>
                      <FileSpreadsheet className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">Export</span>
                    </Button>
                    <Button onClick={() => setShowAddLOModal(true)} className="bg-emerald-600 hover:bg-emerald-700" size="sm">
                      <Plus className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">Add LO Code</span>
                    </Button>
                    <Button onClick={fetchLOCodes} variant="outline" size="sm">
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0 md:p-6 md:pt-0">
                {loLoading ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-navy" />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">LO Code</TableHead>
                          <TableHead className="text-xs">Description</TableHead>
                          <TableHead className="text-xs hidden md:table-cell">Description (Odia)</TableHead>
                          <TableHead className="text-xs">Subject</TableHead>
                          <TableHead className="text-xs">Class</TableHead>
                          <TableHead className="text-right text-xs">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loCodes.length > 0 ? loCodes.map(code => (
                          <TableRow key={code.id}>
                            <TableCell className="font-mono text-xs font-bold text-navy">{code.lo_code}</TableCell>
                            <TableCell className="text-xs max-w-[200px] truncate">{code.lo_description}</TableCell>
                            <TableCell className="text-xs hidden md:table-cell max-w-[150px] truncate">{code.lo_description_odia || '-'}</TableCell>
                            <TableCell className="text-xs">{code.subject}</TableCell>
                            <TableCell className="text-xs">{code.class_name}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-8 w-8 p-0"
                                  onClick={() => openEditLOModal(code)}
                                  title="Edit LO Code"
                                >
                                  <Edit className="w-4 h-4 text-amber-500" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-8 w-8 p-0"
                                  onClick={() => handleDeleteLOCode(code.id)}
                                  title="Delete LO Code"
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )) : (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                              <BookOpen className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                              <p>No LO codes found</p>
                              <p className="text-xs mt-1">Upload Excel or add manually</p>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                )}
                {loCodes.length > 0 && (
                  <div className="p-4 text-sm text-slate-500 border-t">
                    Showing {loCodes.length} LO code(s) {loFilter.class_name && `for Class ${loFilter.class_name}`} {loFilter.subject && `in ${loFilter.subject}`}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

            {/* Contact Info */}
            <Card className="mt-6">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <span className="text-sm text-slate-600">
                    Contact users at: <a href="mailto:teacherutilitytoolkit@gmail.com" className="text-navy font-medium">teacherutilitytoolkit@gmail.com</a>
                  </span>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>

      {/* Create User Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>School Name *</Label>
              <Input 
                value={newUser.school_name}
                onChange={(e) => setNewUser({...newUser, school_name: e.target.value})}
                placeholder="Enter school name"
              />
            </div>
            <div>
              <Label>UDISE Code *</Label>
              <Input 
                value={newUser.udise_code}
                onChange={(e) => setNewUser({...newUser, udise_code: e.target.value.toUpperCase()})}
                placeholder="Enter UDISE code"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cluster</Label>
                <Input 
                  value={newUser.cluster}
                  onChange={(e) => setNewUser({...newUser, cluster: e.target.value})}
                />
              </div>
              <div>
                <Label>Block</Label>
                <Input 
                  value={newUser.block}
                  onChange={(e) => setNewUser({...newUser, block: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>District</Label>
                <Input 
                  value={newUser.district}
                  onChange={(e) => setNewUser({...newUser, district: e.target.value})}
                />
              </div>
              <div>
                <Label>Mobile</Label>
                <Input 
                  value={newUser.mobile}
                  onChange={(e) => setNewUser({...newUser, mobile: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Password *</Label>
              <div className="relative">
                <Input 
                  type={showPassword ? 'text' : 'password'}
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  placeholder="Min 6 characters"
                />
                <button 
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreateUser} disabled={modalLoading} className="bg-emerald-600 hover:bg-emerald-700">
              {modalLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              <span className="text-slate-500">UDISE:</span> <span className="font-mono font-bold">{selectedUser?.udise_code}</span>
            </div>
            <div>
              <Label>School Name</Label>
              <Input 
                value={editUser.school_name}
                onChange={(e) => setEditUser({...editUser, school_name: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cluster</Label>
                <Input 
                  value={editUser.cluster}
                  onChange={(e) => setEditUser({...editUser, cluster: e.target.value})}
                />
              </div>
              <div>
                <Label>Block</Label>
                <Input 
                  value={editUser.block}
                  onChange={(e) => setEditUser({...editUser, block: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>District</Label>
                <Input 
                  value={editUser.district}
                  onChange={(e) => setEditUser({...editUser, district: e.target.value})}
                />
              </div>
              <div>
                <Label>Mobile</Label>
                <Input 
                  value={editUser.mobile}
                  onChange={(e) => setEditUser({...editUser, mobile: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Payment Status</Label>
              <select 
                className="w-full px-3 py-2 border rounded-md"
                value={editUser.payment_status}
                onChange={(e) => setEditUser({...editUser, payment_status: e.target.value})}
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleEditUser} disabled={modalLoading} className="bg-amber-600 hover:bg-amber-700">
              {modalLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Edit className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              <div><span className="text-slate-500">School:</span> {selectedUser?.school_name}</div>
              <div><span className="text-slate-500">UDISE:</span> <span className="font-mono">{selectedUser?.udise_code}</span></div>
            </div>
            <div>
              <Label>New Password</Label>
              <div className="relative">
                <Input 
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 6 characters"
                />
                <button 
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                </button>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              After resetting, communicate the new password to the user via email at teacherutilitytoolkit@gmail.com
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordModal(false)}>Cancel</Button>
            <Button onClick={handleResetPassword} disabled={modalLoading} className="bg-purple-600 hover:bg-purple-700">
              {modalLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-slate-500 text-xs mb-1">School Name</div>
                  <div className="font-medium">{selectedUser.school_name}</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-slate-500 text-xs mb-1">UDISE Code</div>
                  <div className="font-mono font-bold text-navy">{selectedUser.udise_code}</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-slate-500 text-xs mb-1">District</div>
                  <div className="font-medium">{selectedUser.district || '-'}</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-slate-500 text-xs mb-1">Block</div>
                  <div className="font-medium">{selectedUser.block || '-'}</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-slate-500 text-xs mb-1">Cluster</div>
                  <div className="font-medium">{selectedUser.cluster || '-'}</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-slate-500 text-xs mb-1">Mobile</div>
                  <div className="font-medium">{selectedUser.mobile || '-'}</div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-slate-500 text-xs mb-1">Payment Status</div>
                  <div className={`font-medium ${selectedUser.payment_status === 'paid' ? 'text-emerald-600' : 'text-red-600'}`}>
                    {selectedUser.payment_status === 'paid' ? 'Paid' : 'Pending'}
                  </div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-slate-500 text-xs mb-1">Registered</div>
                  <div className="font-medium">{new Date(selectedUser.created_at).toLocaleDateString()}</div>
                </div>
              </div>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                <p className="text-amber-800">
                  <strong>Note:</strong> Password is securely hashed. Use "Reset Password" to set a new password for this user.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsModal(false)}>Close</Button>
            <Button onClick={() => { setShowDetailsModal(false); openEditModal(selectedUser); }} className="bg-amber-600 hover:bg-amber-700">
              <Edit className="w-4 h-4 mr-2" />
              Edit User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add LO Code Modal */}
      <Dialog open={showAddLOModal} onOpenChange={setShowAddLOModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              Add LO Code
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Class *</Label>
                <Select value={newLOCode.class_name} onValueChange={(v) => setNewLOCode({...newLOCode, class_name: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CLASSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subject *</Label>
                <Select value={newLOCode.subject} onValueChange={(v) => setNewLOCode({...newLOCode, subject: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUBJECTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>LO Code *</Label>
              <Input 
                value={newLOCode.lo_code}
                onChange={(e) => setNewLOCode({...newLOCode, lo_code: e.target.value.toUpperCase()})}
                placeholder="e.g., M401"
              />
            </div>
            <div>
              <Label>Description *</Label>
              <Input 
                value={newLOCode.lo_description}
                onChange={(e) => setNewLOCode({...newLOCode, lo_description: e.target.value})}
                placeholder="e.g., Understanding place value"
              />
            </div>
            <div>
              <Label>Description (Odia)</Label>
              <Input 
                value={newLOCode.lo_description_odia}
                onChange={(e) => setNewLOCode({...newLOCode, lo_description_odia: e.target.value})}
                placeholder="e.g., ସ୍ଥାନ ମୂଲ୍ୟ ବୁଝିବା"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLOModal(false)}>Cancel</Button>
            <Button onClick={handleAddLOCode} disabled={modalLoading} className="bg-emerald-600 hover:bg-emerald-700">
              {modalLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add LO Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit LO Code Modal */}
      <Dialog open={showEditLOModal} onOpenChange={setShowEditLOModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit LO Code
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Class *</Label>
                <Select value={newLOCode.class_name} onValueChange={(v) => setNewLOCode({...newLOCode, class_name: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CLASSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subject *</Label>
                <Select value={newLOCode.subject} onValueChange={(v) => setNewLOCode({...newLOCode, subject: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUBJECTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>LO Code *</Label>
              <Input 
                value={newLOCode.lo_code}
                onChange={(e) => setNewLOCode({...newLOCode, lo_code: e.target.value.toUpperCase()})}
                placeholder="e.g., M401"
              />
            </div>
            <div>
              <Label>Description *</Label>
              <Input 
                value={newLOCode.lo_description}
                onChange={(e) => setNewLOCode({...newLOCode, lo_description: e.target.value})}
                placeholder="e.g., Understanding place value"
              />
            </div>
            <div>
              <Label>Description (Odia)</Label>
              <Input 
                value={newLOCode.lo_description_odia}
                onChange={(e) => setNewLOCode({...newLOCode, lo_description_odia: e.target.value})}
                placeholder="e.g., ସ୍ଥାନ ମୂଲ୍ୟ ବୁଝିବା"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditLOModal(false)}>Cancel</Button>
            <Button onClick={handleEditLOCode} disabled={modalLoading} className="bg-amber-600 hover:bg-amber-700">
              {modalLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Edit className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
